import React, { useEffect, useState } from 'react';
import axios from 'axios';

type LeadFormProps = {
    minMass: number;
    maxMass: number;
    minPower: number;
    maxPower: number;
    configBase: string;
    PWRMultiplier: number;
    qty: string;
};

const LeadForm: React.FC<LeadFormProps> = ({
                                               minMass,
                                               maxMass,
                                               minPower,
                                               maxPower,
                                               configBase,
                                               PWRMultiplier,
                                               qty
                                           }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [company, setCompany] = useState('');
    const [notes, setNotes] = useState('');
    const [configuration, setConfiguration] = useState(configBase);
    const [totMinMass, setTotMinMass] = useState(minMass);
    const [totMaxMass, setTotMaxMass] = useState(maxMass);
    const [totMinPower, setTotMinPower] = useState(minPower * PWRMultiplier);
    const [totMaxPower, setTotMaxPower] = useState(maxPower * PWRMultiplier);
    const [interestedQuantity, setInterestedQuantity] = useState(qty.toString());
    const [satQuantity, setSatQuantity] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        setTotMinMass(minMass);
        setTotMaxMass(maxMass);
        setTotMinPower(minPower * PWRMultiplier || 0);
        setTotMaxPower(maxPower * PWRMultiplier || 0);
        setConfiguration(configBase);
        setInterestedQuantity(qty.toString());
    }, [minMass, maxMass, minPower, maxPower, configBase, PWRMultiplier, qty]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError('');
        setSuccess('');

        try {
            await axios.post('/api/submit-lead', {
                name,
                email,
                phone,
                company,
                notes,
                configuration,
                totMinMass,
                totMaxMass,
                totMinPower,
                totMaxPower,
                interestedQuantity,
                satQuantity
            });

            setSuccess('Request sent successfully!');
            setName('');
            setEmail('');
            setPhone('');
            setCompany('');
            setNotes('');
            setSatQuantity('');
        } catch (err) {
            if (axios.isAxiosError(err)) {
                setError(err.response?.data?.error || err.message);
            } else {
                setError('Failed to create lead. Please try again.');
            }
        }
    };

    return (
        <div className="max-w-md mx-auto mt-10 p-3 bg-transparent text-white uppercase">
            <h2 className="text-2xl font-bold mb-6 text-left">get your model</h2>
            <p className="my-4">
                Receive the results of your configuration, such as power and mass directly in your inbox.
            </p>

            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label className="block">Name</label>
                    <input
                        type="text"
                        className="w-full px-4 py-2 border bg-neutral-700"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />
                </div>

                <div className="mb-4">
                    <label className="block">Email</label>
                    <input
                        type="email"
                        className="w-full px-4 py-2 border bg-neutral-700"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>

                <div className="mb-4">
                    <label className="block">Phone number (optional)</label>
                    <input
                        type="text"
                        className="w-full px-4 py-2 border bg-neutral-700"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    />
                </div>

                <div className="mb-4">
                    <label className="block">Company / affiliation</label>
                    <input
                        type="text"
                        className="w-full px-4 py-2 border bg-neutral-700"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        required
                    />
                </div>

                <div className="mb-4">
                    <label className="block">
                        NUMBER OF SATELLITES PLANNED FOR THIS PROJECT
                    </label>
                    <select
                        className="w-full px-4 py-2 border bg-neutral-700"
                        value={satQuantity}
                        onChange={(e) => setSatQuantity(e.target.value)}
                        required
                    >
                        <option value="1">1</option>
                        <option value="2-5">2-5</option>
                        <option value="6-10">6-10</option>
                        <option value="11-50">11-50</option>
                        <option value="50-100">50-100</option>
                        <option value=">100">100+</option>
                    </select>
                </div>

                <div className="mb-4">
                    <label className="block">Additional notes</label>
                    <input
                        type="text"
                        className="w-full px-4 py-2 border bg-neutral-700"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </div>

                <input type="hidden" value={configuration} />
                <input type="hidden" value={totMinMass} />
                <input type="hidden" value={totMaxMass} />
                <input type="hidden" value={totMinPower} />
                <input type="hidden" value={totMaxPower} />
                <input type="hidden" value={interestedQuantity} />

                {error && <p className="text-red-500">{error}</p>}
                {success && <p className="text-blue-400">{success}</p>}

                <button
                    type="submit"
                    className="w-full rounded-full text-white py-2 uppercase font-bold"
                    style={{
                        background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
                    }}
                >
                    Get your model specs
                </button>
            </form>
        </div>
    );
};

export default LeadForm;
